// One guardrail (03-the-guardrails-service.md § the console): the selector,
// the expression in a monospace field, the escalation block and the
// transformer block as forms, and the service's refusal beside the field it
// names; switch off and on; *Delete* last in the page's menu, asked first
// (01-the-console.md § conventions: remove).

import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { tenantPath } from "../../address/address.ts";
import { isRefusal } from "../../platform/bus.ts";
import { Button, Empty, NotAllowed, Notice, PageHead, Pending, Pill, useLoad } from "../../ui/components.tsx";
import { Code, ruleSubject, SERVICE, validID, type Rule, type RuleEntry } from "../../wire/guard.ts";
import { RowMenu, type MenuEntry } from "../../ui/table.tsx";
import { useSurface } from "../surface.tsx";
import { GuardRefusal } from "./Unserved.tsx";
import { draftOf, refusalFields, RuleForm, ruleOf, type Draft } from "./RuleForm.tsx";
import { guards, type RuleField } from "./words.ts";

export function GuardrailPage() {
  const { id = "" } = useParams();
  const s = useSurface();
  if (!validID(id)) {
    return (
      <PageHead
        crumb={<Link to={tenantPath(s.tenant, "guardrails")}>Guardrails</Link>}
        title="Not a guardrail"
        line={`"${id}" is not a guardrail's name.`}
      />
    );
  }
  return <One id={id} />;
}

function One({ id }: { id: string }) {
  const s = useSurface();
  const navigate = useNavigate();
  const [load, reload] = useLoad(`guardrails/${id}`, () =>
    s.bus.call<RuleEntry>(SERVICE, ruleSubject("get", id), undefined),
  );
  const crumb = (
    <>
      <Link to={tenantPath(s.tenant, "guardrails")}>Guardrails</Link>
      <span>/</span>
      <span>{id}</span>
    </>
  );
  if (load.state === "pending") {
    return (
      <>
        <div className="crumb">{crumb}</div>
        <Pending what="Reading the guardrail" />
      </>
    );
  }
  if (load.state === "refused") {
    return (
      <>
        <PageHead crumb={crumb} title={id} />
        {isRefusal(load.err, Code.notFound) ? (
          <Empty>No guardrail by this name.</Empty>
        ) : (
          <GuardRefusal
            err={load.err}
            what="This guardrail"
            absent="The guardrails service does not run in this install, so no guardrail can be read."
          />
        )}
      </>
    );
  }
  return (
    <Editor
      key={load.value.revision}
      entry={load.value}
      crumb={crumb}
      reload={reload}
      onDeleted={() => void navigate(tenantPath(s.tenant, "guardrails"))}
    />
  );
}

function Editor({
  entry,
  crumb,
  reload,
  onDeleted,
}: {
  entry: RuleEntry;
  crumb: React.ReactNode;
  reload: () => void;
  onDeleted: () => void;
}) {
  const s = useSurface();
  const rule = entry.rule;
  const [draft, setDraft] = useState<Draft>(draftOf(rule));
  const [refused, setRefused] = useState<Partial<Record<RuleField, string>>>({});
  const [err, setErr] = useState<unknown>(undefined);
  const [note, setNote] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const write = (r: Rule, said: string) => {
    setBusy(true);
    setErr(undefined);
    setRefused({});
    setNote(undefined);
    s.bus.call<RuleEntry>(SERVICE, ruleSubject("set", r.id), r).then(
      (saved) => {
        setBusy(false);
        setNote(`${said} Version ${saved.revision}; every workload applies it within moments.`);
        reload();
      },
      (e: unknown) => {
        setBusy(false);
        const fields = refusalFields(e);
        setRefused(fields);
        if (Object.keys(fields).length === 0) {
          setErr(e);
        }
      },
    );
  };
  const save = () => {
    if (busy) {
      return;
    }
    write(ruleOf({ ...draft, id: rule.id }), "Saved.");
  };
  const toggle = () => {
    write({ ...rule, enabled: !rule.enabled }, rule.enabled ? "Switched off." : "Switched on.");
  };
  const remove = () => {
    setBusy(true);
    setErr(undefined);
    s.bus.call<RuleEntry>(SERVICE, ruleSubject("delete", rule.id), undefined).then(
      () => {
        setBusy(false);
        onDeleted();
      },
      (e: unknown) => {
        setBusy(false);
        setErr(e);
      },
    );
  };

  return (
    <>
      <PageHead
        crumb={crumb}
        title={rule.id}
        line={`Guards ${guards(rule.selector)}.`}
        action={
          <>
            <Button onClick={toggle} disabled={busy}>
              {rule.enabled ? "Switch off" : "Switch on"}
            </Button>
            <Button kind="primary" onClick={save} disabled={busy}>
              {busy ? "Saving…" : "Save"}
            </Button>
            <RowMenu label={`More for ${rule.id}`} entries={busy ? [] : pageMenu(rule.id, remove)} />
          </>
        }
      />
      <div className="row" style={{ marginBottom: 20 }}>
        {rule.enabled ? <Pill tone="live">on</Pill> : <Pill>off</Pill>}
        <span className="small muted">version {entry.revision}</span>
      </div>
      {note !== undefined && <Notice kind="ok">{note}</Notice>}
      {err !== undefined && <NotAllowed err={err} />}
      <div className="card">
        <RuleForm draft={draft} onChange={setDraft} refused={refused} editing onSubmit={save} />
      </div>
    </>
  );
}

/** pageMenu is a guardrail's page menu: *Delete*, last, asked first. */
export function pageMenu(id: string, remove: () => void): MenuEntry[] {
  return [
    {
      label: "Delete",
      danger: true,
      confirm: {
        title: `Delete guardrail ${id}?`,
        consequence:
          "For good: every workload drops it within moments, and nothing it held waits any longer.",
      },
      onSelect: remove,
    },
  ];
}
